// app/src/lib/revenue/waste.js — waste / spoilage logging (US-4.5).
//
// Each waste entry is valued at the item's current best unit price at time of
// entry; the valued amount is stored on the row so retroactive price changes
// don't rewrite historical waste cost.

import { db } from '../db.js';
import { requireUserId } from '../procurement/util.js';
import { mutate } from '../scale/instrument.js';

const REASONS = new Set(['spoilage', 'overcooking', 'breakage', 'comp', 'other']);

/** @typedef {{ wasteId:string, userId:string, itemId:string, quantity:number, uom:string, reason:string, date:string, valuedCents:number, notes?:string }} WasteEntry */

export async function logWaste(input) {
  const userId = await requireUserId();
  if (!input.itemId) throw new Error('waste: itemId required');
  if (!Number.isFinite(input.quantity) || input.quantity <= 0) throw new Error('waste: quantity > 0 required');
  if (!input.date || !/^\d{4}-\d{2}-\d{2}$/.test(input.date)) throw new Error('waste: date YYYY-MM-DD required');
  const reason = REASONS.has(input.reason) ? input.reason : 'other';

  const item = await db.items.get(input.itemId);
  if (!item || item.userId !== userId) throw new Error('waste: item not found');

  // Value at lowest current unit price across vendors (mirrors recipes.plateCost).
  const prices = await db.priceEntries.where('itemId').equals(input.itemId).filter((p) => p.userId === userId).toArray();
  let bestUnit = 0;
  if (prices.length) {
    const latestByVendor = new Map();
    for (const p of prices) {
      const cur = latestByVendor.get(p.vendorId);
      if (!cur || p.effectiveDate > cur.effectiveDate) latestByVendor.set(p.vendorId, p);
    }
    const cands = [...latestByVendor.values()].map((p) => p.unitPriceCents ?? p.priceCents);
    if (cands.length) bestUnit = Math.min(...cands);
  }

  /** @type {WasteEntry} */
  const row = {
    wasteId: input.wasteId || crypto.randomUUID(),
    userId,
    itemId: input.itemId,
    quantity: Number(input.quantity),
    uom: input.uom || item.defaultUom || 'each',
    reason,
    date: input.date,
    valuedCents: Math.round(bestUnit * input.quantity),
    notes: input.notes,
  };
  return mutate(
    { entityType: 'wasteEntry', entityId: row.wasteId, action: 'create', after: row },
    async () => { await db.wasteEntries.put(row); return row; },
  );
}

export async function remove(wasteId) {
  const userId = await requireUserId();
  const existing = await db.wasteEntries.get(wasteId);
  if (!existing || existing.userId !== userId) return;
  await mutate(
    { entityType: 'wasteEntry', entityId: wasteId, action: 'delete', before: existing },
    async () => { await db.wasteEntries.delete(wasteId); },
  );
}

/**
 * Weekly summary: by item, by reason, by category.
 * Returns totals for the supplied date range.
 */
export async function summary({ from, to }) {
  const userId = await requireUserId();
  const rows = await db.wasteEntries
    .where('[userId+date]').between([userId, from], [userId, to], true, true)
    .toArray();
  const itemIds = Array.from(new Set(rows.map((r) => r.itemId)));
  const items = itemIds.length ? await db.items.where('itemId').anyOf(itemIds).toArray() : [];
  const itemById = new Map(items.map((i) => [i.itemId, i]));

  const byItem = new Map(), byReason = new Map(), byCategory = new Map();
  let totalCents = 0;
  for (const r of rows) {
    totalCents += r.valuedCents || 0;
    const it = itemById.get(r.itemId);
    addBucket(byItem, r.itemId, { label: it?.name || 'Unknown', cents: r.valuedCents });
    addBucket(byReason, r.reason, { label: r.reason, cents: r.valuedCents });
    addBucket(byCategory, it?.category || 'other', { label: it?.category || 'other', cents: r.valuedCents });
  }

  return {
    from, to, totalCents,
    byItem: [...byItem.values()].sort((a, b) => b.totalCents - a.totalCents),
    byReason: [...byReason.values()].sort((a, b) => b.totalCents - a.totalCents),
    byCategory: [...byCategory.values()].sort((a, b) => b.totalCents - a.totalCents),
  };
}

function addBucket(map, key, { label, cents }) {
  const cur = map.get(key) || { key, label, totalCents: 0 };
  cur.totalCents += cents || 0;
  map.set(key, cur);
}

/**
 * Waste as % of total COGS purchases for the period.
 */
export async function wastePctOfPurchases({ from, to }) {
  const userId = await requireUserId();
  const s = await summary({ from, to });
  const invoices = await db.invoices.where('userId').equals(userId)
    .filter((i) => i.issuedDate >= from && i.issuedDate <= to)
    .toArray();
  const purchasesCents = invoices.reduce((sum, i) => sum + (i.totalCents || 0), 0);
  return {
    purchasesCents,
    wasteCents: s.totalCents,
    pct: purchasesCents > 0 ? s.totalCents / purchasesCents : null,
  };
}
